import styled from '@emotion/styled';
import moment from 'moment';
import React, { ReactElement } from 'react';

import { BillingStatus } from '../BillingStatus/component';
import { UserSubscriptionPlanDetail } from '../../../containers/SubscriptionsAdmin/types';
import { OL_COLOUR_KELP } from '../../Style/colours';
import { PaymentMethodSummary } from '../../../../ReduxStorage/UserSubscriptionPlans/constants';
import { capitalise } from '../../../utilities/legacy-utilities';

const THeadDataCell = styled('div')({
  width: '100%',
  fontSize: 14,
  fontWeight: 'bold',
  padding: 8,

  '@media (min-width: 768px)': {
    borderBottom: '1px solid #D8D8D8',
    padding: 16,
  },
});

const StyledTD = styled('td')({
  padding: 12,
  fontSize: 14,
  maxWidth: 180,
});

const StyledTH = styled('th')({
  padding: 12,
  fontSize: 14,
  textAlign: 'left',
  maxWidth: 180,
});

const TBodyDataCell = styled('div')({
  width: '100%',
  fontSize: 14,
  padding: 8,
});

const TableColumn = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  borderBottom: '1px solid #D8D8D8',
  padding: 4,

  '&:last-child': {
    borderBottom: 'none',
  },

  '@media (min-width: 768px)': {
    borderBottom: 'none',
    padding: 0,
  },
});

const Container = styled('div')({
  border: '1px solid #D8D8D8',
  borderRadius: 4,
});

const ContainerMobile = styled(Container)({
  '@media (min-width: 768px)': {
    display: 'none',
  },
});

const ContainerDesktop = styled(Container)({
  display: 'none',
  padding: 4,

  '@media (min-width: 768px)': {
    display: 'block',
  },
});

const ButtonsContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',

  'button:first-child': {
    marginTop: 0,
  },

  button: {
    marginTop: 12,
    maxWidth: 195,
  },
});

const BillingStatusNote = styled('div')({
  marginTop: 8,
});

const StyledLink = styled('a')({
  color: OL_COLOUR_KELP,
});

interface BillingDetailsProps {
  plan: UserSubscriptionPlanDetail;
  paymentMethodSummary: PaymentMethodSummary | null;
  isOwner: boolean;
  actions: ReactElement[];
}

/* eslint-disable @typescript-eslint/no-use-before-define */
export const BillingDetailsTable = React.memo(
  ({ plan, isOwner, actions, paymentMethodSummary }: BillingDetailsProps) => (
    <>
      <BillingDetailsTableDesktop
        isOwner={isOwner}
        plan={plan}
        actions={actions}
        paymentMethodSummary={paymentMethodSummary}
      />
      <BillingDetailsTableMobile
        isOwner={isOwner}
        plan={plan}
        actions={actions}
        paymentMethodSummary={paymentMethodSummary}
      />
    </>
  )
);
/* eslint-enable @typescript-eslint/no-use-before-define */

const StyledPaymentSpan = styled('span')({
  display: 'block',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',
});

const getPaymentMethodLabel = (
  paymentMethodSummary: PaymentMethodSummary | null
): ReactElement | string => {
  if (paymentMethodSummary === null) {
    return 'n/a';
  }

  const {
    billingMethod,
    cardType,
    cardholderName,
    expiryDate,
    cardNumber,
  } = paymentMethodSummary;

  if (billingMethod === 'invoice') {
    return 'Invoice';
  }

  const values = [];

  if (cardholderName) {
    values.push(`Billed to ${cardholderName}`);
  }

  if (cardType && cardNumber) {
    values.push(`${capitalise(cardType)} ending ${cardNumber}`);
  }

  if (expiryDate) {
    values.push(`Expiry ${expiryDate}`);
  }

  return (
    <>
      {values.length > 0 ? (
        values.map((value) => <StyledPaymentSpan>{value}</StyledPaymentSpan>)
      ) : (
        <span>n/a</span>
      )}
    </>
  );
};

const renderAmountLabel = ({
  currency,
  price,
  paymentInterval,
  type,
  billingCycle,
  billingMethod,
}: UserSubscriptionPlanDetail): ReactElement => {
  if (type === 'custom') {
    return (
      <StyledLink
        href="https://solutions.openlearning.com/contact"
        target="_blank"
        rel="noreferrer"
      >
        Contact us
      </StyledLink>
    );
  }

  if (currency && price) {
    const priceLabel = `${currency === 'MYR' ? '' : '$'}${price}`;
    const paymentIntervalLabel = `${
      paymentInterval === null
        ? billingMethod === 'braintree' && billingCycle
          ? `(${billingCycle})`
          : ''
        : `(${paymentInterval})`
    }`;
    return <p>{`${currency} ${priceLabel} ${paymentIntervalLabel}`}</p>;
  }

  return <p>No price</p>;
};

const BillingDetailsTableDesktop = React.memo(
  ({ plan, isOwner, actions, paymentMethodSummary }: BillingDetailsProps) => {
    return (
      <ContainerDesktop>
        <tr>
          <StyledTH>Current subscription</StyledTH>
          <StyledTH>Status</StyledTH>
          <StyledTH>Next payment</StyledTH>
          <StyledTH>Amount</StyledTH>
          <StyledTH>Payment method</StyledTH>
          <StyledTH>Actions</StyledTH>
        </tr>
        <tr>
          <StyledTD>
            {(plan.type === 'personal' && plan.billingMethod === 'stripe') ||
            (plan.type === 'portal' &&
              (plan.billingMethod === 'stripe' ||
                plan.billingMethod === 'invoice')) ? (
              <StyledLink
                href={`https://solutions.openlearning.com/pricing`}
                target="_blank"
                rel="noreferrer"
              >
                {plan.name}
              </StyledLink>
            ) : (
              plan.name
            )}
          </StyledTD>
          <StyledTD>
            <BillingStatus
              status={plan.status}
              label={plan.status.toLocaleUpperCase()}
              cancelAtPeriodEnd={plan.cancelAtPeriodEnd}
            />
            <BillingStatusNote>
              {plan.status !== 'cancelled' && plan.cancelAtPeriodEnd && (
                <div>Can access until</div>
              )}
              {plan.nextPayment &&
                moment(plan.nextPayment).format('DD MMM YYYY')}
            </BillingStatusNote>
          </StyledTD>
          <StyledTD>
            {plan.cancelAtPeriodEnd
              ? 'N/A'
              : plan.nextPayment &&
                moment(plan.nextPayment).format('DD MMM YYYY')}
          </StyledTD>
          <StyledTD>{renderAmountLabel(plan)}</StyledTD>
          <StyledTD>{getPaymentMethodLabel(paymentMethodSummary)}</StyledTD>
          {isOwner && (
            <StyledTD>
              <ButtonsContainer>{actions}</ButtonsContainer>
            </StyledTD>
          )}
        </tr>
      </ContainerDesktop>
    );
  }
);

const BillingDetailsTableMobile = React.memo(
  ({ plan, isOwner, actions, paymentMethodSummary }: BillingDetailsProps) => {
    return (
      <ContainerMobile>
        <TableColumn>
          <TableColumn>
            <THeadDataCell>Current subscription</THeadDataCell>
            <TBodyDataCell>
              {(plan.type === 'personal' && plan.billingMethod === 'stripe') ||
              (plan.type === 'portal' &&
                (plan.billingMethod === 'stripe' ||
                  plan.billingMethod === 'invoice')) ? (
                <StyledLink
                  href={`https://solutions.openlearning.com/pricing`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {plan.name}
                </StyledLink>
              ) : (
                plan.name
              )}
            </TBodyDataCell>
          </TableColumn>
          <TableColumn>
            <THeadDataCell>Status</THeadDataCell>
            <TBodyDataCell>
              <BillingStatus
                status={plan.status}
                label={plan.status.toLocaleUpperCase()}
              />
            </TBodyDataCell>
          </TableColumn>
          <TableColumn>
            <THeadDataCell>Next payment</THeadDataCell>
            <TBodyDataCell>
              {plan.nextPayment &&
                moment(plan.nextPayment).format('DD MMM YYYY')}
            </TBodyDataCell>
          </TableColumn>
          <TableColumn>
            <THeadDataCell>Amount</THeadDataCell>
            <TBodyDataCell>{renderAmountLabel(plan)}</TBodyDataCell>
          </TableColumn>
          <TableColumn>
            <THeadDataCell>Payment method</THeadDataCell>
            <TBodyDataCell>
              {getPaymentMethodLabel(paymentMethodSummary)}
            </TBodyDataCell>
          </TableColumn>
          {isOwner && (
            <TableColumn>
              <THeadDataCell>Actions</THeadDataCell>
              <TBodyDataCell>
                <ButtonsContainer>{actions}</ButtonsContainer>
              </TBodyDataCell>
            </TableColumn>
          )}
        </TableColumn>
      </ContainerMobile>
    );
  }
);
